/**
 * Contacts tab — the people attached to this loan.
 *
 * One page, three sections, and a sub-view strip under the page title. The strip is the
 * SAME flat underline strip the Documents and Closing tabs render
 * (`DrilldownSubTabStrip`), but because Contacts is one page rather than three
 * routes, choosing a section SCROLLS to it instead of navigating; the strip
 * then follows the scroll position. The primary nav's hover flyout offers the
 * same three entries and deep-links with `?section=<key>` — both read
 * `contactsSections.js`, which is what keeps them in step.
 *
 *   Key roles     — every canonical role as a slot, filled or not, over a
 *                   segmented meter. An unfilled slot is as visible as a filled
 *                   one; assigning opens the assign dialog with the role fixed.
 *   General       — construction products only. The GC roster, their UW
 *   contractors     confirmations and experience, beside the doc group those
 *                   confirmations rest on.
 *   All contacts  — the full list. Row click opens the edit panel.
 *
 * General Contractor is NOT a key role: a GC is a `generalContractors[]` record
 * with confirmations and an experience rollup, so it gets its own section
 * rather than a slot that could only ever show a name.
 *
 * Writes:
 *   assign / change a role   → the assign dialog (add_loan_related_contact /
 *                              update_loan_related_contact)
 *   remove from the loan     → delete_loan_related_contact, behind a
 *                              confirmation — it is a real delete (the backend
 *                              keeps a soft-delete copy in
 *                              `deletedLoanRelatedContacts[]`), not an unassign
 *   edit everything else     → DrilldownSidebarAddLoanRelatedContact, which
 *                              treats initialValues carrying an `_id` as edit
 *                              mode and a bare `{ loanRole }` as create.
 */
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import PersonOffOutlined from "@mui/icons-material/PersonOffOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { Box, InputAdornment, TextField } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { detailSidebarClear } from "../../../../redux/reducers/detailSidebarReducer";
import DrilldownSubTabStrip from "../../components/DrilldownSubTabStrip";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import SectionEmptyState from "../../shared/tabLayout/SectionEmptyState";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import { level1 } from "../../../../theme/colors";
import { formSx } from "../../../../theme/formStyles";
import { FixedUploadQueue } from "../LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";
import { UploadQueueProvider } from "../LoanDrilldownDocumentsTab/UploadQueueContext";
import GeneralContractorsSection from "./GeneralContractorsSection";
import AllContactsList from "./components/AllContactsList";
import ContactRoleAssignDialog from "./components/ContactRoleAssignDialog";
import KeyRolesSection from "./components/KeyRolesSection";
import { contactDisplayName } from "./components/KeyRoleSlotCard";
import {
  CONTACTS_SECTION_ANCHOR_SX,
  CONTACTS_SECTION_PARAM,
  CONTACT_SECTIONS,
  contactsSectionAnchor,
  getContactsSections,
  isConstructionProduct,
} from "./contactsSections";

// Canonical role names — must match values in selectOptionsContactRole so that
// prefilling the role on the add-contact form binds to the select. General
// Contractor is deliberately absent (see the header comment).
const KEY_ROLES = [
  "Title Agent",
  "Settlement Agent",
  "Insurance Agent",
  "Appraiser",
  "Broker Contact",
  "Buyer Real Estate Agent",
];

const LoanDrilldownLoanRelatedContactsTab = memo(() => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteLoanRelatedContact } = useUnderwritingHook();
  const [query, setQuery] = useState("");

  const dealId = useSelector((state) => state?.loanDrilldown?._id);
  const contacts = useSelector(
    (state) => state?.loanDrilldown?.loanRelatedContacts || [],
  );
  const productType = useSelector(
    (state) => state?.loanDrilldown?.loanProductType,
  );

  // Highlight whichever contact is open in the ADD_LOAN_RELATED_CONTACT sidebar
  // (it doubles as the edit sidebar).
  const editingId = useSelector((state) => {
    if (
      state.sidebar?.type !== loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT
    )
      return null;
    return state.sidebar?.state?._id || null;
  });

  const isConstruction = useMemo(
    () => isConstructionProduct(productType),
    [productType],
  );

  const sections = useMemo(
    () => getContactsSections({ isConstruction }),
    [isConstruction],
  );

  /* ── sidebars ─────────────────────────────────────────────────────────── */

  const openEditContact = useCallback(
    (contact) => {
      if (!contact?._id) return;
      dispatch(detailSidebarClear());
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
      );
      dispatch(sidebarSetStateValue(contact));
    },
    [dispatch],
  );

  const openAddContact = useCallback(
    (prefilledRole) => {
      dispatch(detailSidebarClear());
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
      );
      // No `_id` on initialValues means create; a partial { loanRole } payload
      // just pre-selects the role.
      dispatch(
        sidebarSetStateValue(
          prefilledRole ? { loanRole: prefilledRole } : null,
        ),
      );
    },
    [dispatch],
  );

  /* ── role slots ───────────────────────────────────────────────────────── */

  // Slot each contact into its canonical role. First match wins; any duplicate
  // is counted on the slot ("+1 more") and still listed in All contacts, so
  // nothing is hidden by this pass.
  const slots = useMemo(() => {
    const used = new Set();
    return KEY_ROLES.map((role) => {
      const holders = contacts.filter(
        (c) => (c?.loanRole || "").toLowerCase() === role.toLowerCase(),
      );
      const match = holders.find((c) => !used.has(c._id)) || null;
      if (match) used.add(match._id);
      return {
        role,
        contact: match,
        extraCount: Math.max(0, holders.length - 1),
      };
    });
  }, [contacts]);

  const missingCount = slots.filter((s) => !s.contact).length;

  // The subtitle states the one thing worth knowing before you read anything
  // else: is the file's roster complete?
  const subTitle = useMemo(() => {
    if (!contacts.length) return "No contacts on this loan yet.";
    if (missingCount > 0)
      return `${missingCount} key ${
        missingCount === 1 ? "role is" : "roles are"
      } still unassigned.`;
    return "All key roles are filled for this loan.";
  }, [contacts.length, missingCount]);

  /* ── assign / change ──────────────────────────────────────────────────── */

  const [assign, setAssign] = useState(null); // { role, contact }

  const openAssign = useCallback(
    (role, contact) => setAssign({ role, contact: contact || null }),
    [],
  );

  /* ── remove ───────────────────────────────────────────────────────────── */

  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const confirmRemove = useCallback(() => {
    const contact = pendingRemove?.contact;
    if (!dealId || !contact?._id || removing) return;
    setRemoving(true);
    deleteLoanRelatedContact({
      dealId,
      contactId: contact._id,
      onSuccessFn: (loan) => {
        if (loan) dispatch(loanDrilldownSet(loan));
        enqueueSnackbar(
          `${contactDisplayName(contact)} removed from the loan.`,
          { variant: "success" },
        );
        setPendingRemove(null);
        setRemoving(false);
      },
      onFailFn: () => setRemoving(false),
    });
  }, [
    dealId,
    pendingRemove,
    removing,
    deleteLoanRelatedContact,
    dispatch,
    enqueueSnackbar,
  ]);

  /* ── section strip: scroll + spy + deep link ──────────────────────────── */

  const [activeSection, setActiveSection] = useState(sections[0]?.key);
  // While a programmatic scroll is running the observer would fight it, so the
  // strip holds the chosen section until the scroll settles.
  const jumpingTo = useRef(null);

  const jumpToSection = useCallback((key) => {
    const el = document.getElementById(contactsSectionAnchor(key));
    if (!el) return;
    jumpingTo.current = key;
    setActiveSection(key);
    el.scrollIntoView({ block: "start", behavior: "smooth" });
    window.setTimeout(() => {
      if (jumpingTo.current === key) jumpingTo.current = null;
    }, 700);
  }, []);

  // Scroll spy. `root: null` watches the viewport, which is the right answer
  // whatever element actually scrolls; the top band keeps "active" meaning
  // "the section under the strip".
  useEffect(() => {
    const nodes = sections
      .map((s) => document.getElementById(contactsSectionAnchor(s.key)))
      .filter(Boolean);
    if (!nodes.length || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (jumpingTo.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (!visible.length) return;
        const id = visible[0].target.id;
        const hit = sections.find((s) => contactsSectionAnchor(s.key) === id);
        if (hit) setActiveSection(hit.key);
      },
      { rootMargin: "-80px 0px -55% 0px", threshold: 0 },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [sections]);

  // Deep link from the nav flyout: `…/contacts?section=<key>`. Consumed once
  // and stripped, so the URL never pins a stale section. (The flyout's "Add
  // contact" is not a link at all — it opens the sidebar in place.)
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get(CONTACTS_SECTION_PARAM);
  useEffect(() => {
    if (!sectionParam) return;
    if (sections.some((s) => s.key === sectionParam)) {
      // One frame, so the sections are mounted before we measure them.
      window.requestAnimationFrame(() => jumpToSection(sectionParam));
    }
    const next = new URLSearchParams(searchParams);
    next.delete(CONTACTS_SECTION_PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionParam]);

  const stripOptions = useMemo(
    () =>
      sections.map((s) => ({
        key: s.key,
        label: s.label,
        icon: s.icon,
        count:
          s.key === CONTACT_SECTIONS.KEY_ROLES
            ? `${slots.length - missingCount}/${slots.length}`
            : s.key === CONTACT_SECTIONS.ALL_CONTACTS
              ? contacts.length
              : undefined,
      })),
    [sections, slots.length, missingCount, contacts.length],
  );

  return (
    // Files chosen on the General Contractor Docs panel stage here and are
    // reviewed / committed from the bottom-docked FixedUploadQueue, exactly
    // like the Documents and Applicants tabs.
    <UploadQueueProvider>
      <LoanTabShell
        title="Loan Related Contacts"
        subTitle={subTitle}
        primaryAction={{
          label: "Add contact",
          icon: <PersonAddOutlined />,
          onClick: () => openAddContact(null),
        }}
      >
        {/* The strip sits under the title, in the content column, and is
            pinned so the jump list stays reachable from anywhere on a long
            page. It is the body's first child rather than part of the header
            so `sticky` holds for the whole stack of cards. No background. */}
        <Box sx={{ position: "sticky", top: 0, zIndex: 3 }}>
          <DrilldownSubTabStrip
            options={stripOptions}
            value={activeSection}
            onChange={jumpToSection}
            // Plain underline tabs here: no row fill, and the active tab is
            // marked by its teal label + underline only (no tint gradient).
            sx={{
              background: "transparent",
              "& .MuiToggleButton-root": {
                borderBottom: "2px solid transparent !important",
                "&:hover": { background: "transparent !important" },
              },
              "& .MuiToggleButton-root.Mui-selected": {
                background: "transparent !important",
                borderBottom: `2px solid ${level1.indicator} !important`,
                "&:hover": { background: "transparent !important" },
              },
            }}
          />
        </Box>

        <KeyRolesSection
          id={contactsSectionAnchor(CONTACT_SECTIONS.KEY_ROLES)}
          slots={slots}
          editingId={editingId}
          onOpenContact={openEditContact}
          onAssignRole={openAssign}
          onRemoveContact={(contact, role) =>
            setPendingRemove({ contact, role })
          }
        />

        {/* General contractors — construction loans only (the GC UW confirmations
            + experience live on the loan's generalContractors[]). Sits above All
            contacts: it's a scoped roster, and All contacts is the catch-all. */}
        {isConstruction && (
          <GeneralContractorsSection
            id={contactsSectionAnchor(CONTACT_SECTIONS.GENERAL_CONTRACTORS)}
          />
        )}

        {/* ── All contacts ──
            Search is card-scoped (it filters this table only), so it sits in the
            card header rather than the page header. */}
        <LoanSectionCard
          id={contactsSectionAnchor(CONTACT_SECTIONS.ALL_CONTACTS)}
          sx={CONTACTS_SECTION_ANCHOR_SX}
          title={
            // Same n pill as Key roles, beside the title.
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              All contacts
              <Box
                component="span"
                aria-label={`${contacts.length} contact${contacts.length === 1 ? "" : "s"}`}
                sx={formSx.sectionCount(false)}
              >
                {contacts.length}
              </Box>
            </Box>
          }
          collapsible
          chevronStart
          subTitle="Select a contact to view or edit their details."
          controls={
            contacts.length > 0 ? (
              <TextField
                size="small"
                value={query}
                placeholder="Search name, company or role"
                onChange={(e) => setQuery(e.target.value)}
                inputProps={{ "aria-label": "Search contacts" }}
                InputProps={{
                  sx: { fontSize: 12.5, height: 32 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded
                        sx={{ fontSize: 16, color: T.color.muted }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 240 }}
              />
            ) : null
          }
          disableBodyPadding={contacts.length > 0}
        >
          {contacts.length === 0 ? (
            <SectionEmptyState
              icon={<PersonOffOutlined />}
              message="Add the people connected to this loan, or fill a role above to start with that role selected."
              action={{
                label: "Add contact",
                icon: <PersonAddOutlined />,
                onClick: () => openAddContact(null),
              }}
            />
          ) : (
            <AllContactsList
              contacts={contacts}
              editingId={editingId}
              quickFilter={query}
              keyRoles={KEY_ROLES}
              onSelect={openEditContact}
            />
          )}
        </LoanSectionCard>
      </LoanTabShell>

      {/* Mounted only while open: both dialogs name the role and the person in
          their heading, and a dialog kept mounted through its own close
          transition renders that heading with nothing in it. */}
      {assign && (
        <ContactRoleAssignDialog
          open
          role={assign.role}
          currentContact={assign.contact}
          contacts={contacts}
          dealId={dealId}
          onClose={() => setAssign(null)}
        />
      )}

      {/* Removing is a real delete of the loan contact (the backend keeps a
          soft-delete copy), so it always asks first. */}
      {pendingRemove && (
        <ConfirmationDialog
          open
          title={`Remove ${contactDisplayName(pendingRemove.contact)} from this loan?`}
          message={`This takes them off the loan entirely, not just off the ${
            pendingRemove.role
          } role. Their details stay in the contact directory, so they can be added back.`}
          confirmText="Remove contact"
          cancelText="Cancel"
          destructive
          loading={removing}
          onConfirm={confirmRemove}
          onCancel={() => (removing ? null : setPendingRemove(null))}
        />
      )}

      <FixedUploadQueue />
    </UploadQueueProvider>
  );
});

LoanDrilldownLoanRelatedContactsTab.displayName =
  "LoanDrilldownLoanRelatedContactsTab";

export { KEY_ROLES };
export default LoanDrilldownLoanRelatedContactsTab;
